import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import Axios from "axios";

import { sweetConfirm } from "../sweet-alert/sweetConfirm";
import addToast from "../../utils/addToast";
import Icon from "../icon";
import { Button } from "..";

import styles from "./Dropdown.module.scss";

interface IProps {
    options: ILabelValue<number>[];
    url?: string;
    permCode?: string;
    handleStepSubmit?: Function;
    refreshData?: Function;
    CustomModalTemplate?: any;
    isIntersectingMenu?: boolean;
    cyId?: string;
    onActionSuccess?: Function;
    disabled?: boolean;
    display?: boolean;
}

type Step = (ILabelValue<number> & { action?: number }) | undefined;

export const StepDropDown = ({
    options = [],
    url,
    handleStepSubmit,
    refreshData,
    permCode = undefined,
    CustomModalTemplate = undefined,
    isIntersectingMenu,
    cyId,
    onActionSuccess = undefined,
    disabled = false,
    display = true,
}: IProps) => {
    const { t } = useTranslation();

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [selectedStep, setSelectedStep] = useState<Step>(undefined);
    const [isBusy, setIsBusy] = useState<boolean>(false);

    const handleStepChange = async (step: ILabelValue<number>) => {
        if (!CustomModalTemplate) handleDefaultStepSubmit(step);
        else setSelectedStep(step);
    };

    const handleDefaultStepSubmit = async (step = selectedStep, additionalParams = {}, e: SyntheticEvent | undefined = undefined) => {
        e && e.preventDefault();
        setIsBusy(true);

        if (handleStepSubmit) {
            await handleStepSubmit(step);
            setIsBusy(false);
            return;
        }

        try {
            if (!url) throw new Error("No url");
            if (!CustomModalTemplate && !(await sweetConfirm(step?.label, { text: "Do you want to proceed?" }))) return;

            await Axios.patch(url, { workflowStep: step?.value, action: step?.action, ...additionalParams });
            refreshData && refreshData();
            onActionSuccess && onActionSuccess();
            addToast({ title: "Operation was success." });

            CustomModalTemplate && handleCloseModal();
        } catch (err: any) {
            console.error(err);
        } finally {
            setIsBusy(false);
        }
    };

    const buttonRef = useRef<HTMLButtonElement>(null);
    const ulRef = useRef<HTMLUListElement>(null);

    const buttonBox = buttonRef.current?.getBoundingClientRect();

    const TOP_OFFSET = 8;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!ulRef.current?.contains(e.target) && e.target != buttonRef.current) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const handleCloseModal = () => setSelectedStep(undefined);

    if (!display) return <></>;

    if (isIntersectingMenu) {
        return (
            <ul>
                {options.map((item, index) => {
                    return (
                        <li key={`${index}-${item.value}`} onClick={() => handleStepChange(item)} className="pointer">
                            <a className="nav-link">
                                <Icon name={["fas", "arrow-right"]} />
                                <span>{t(item.label)}</span>
                                <span className="rui-nav-circle"></span>
                            </a>
                            {selectedStep && (
                                <CustomModalTemplate
                                    handleStepSubmit={handleDefaultStepSubmit}
                                    step={selectedStep}
                                    handleCloseModal={handleCloseModal}
                                    isBusy={isBusy}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    }

    return (
        <>
            {options.length == 1 ? (
                <Button
                    text={options[0].label}
                    permCode={permCode}
                    onClick={() => handleStepChange(options[0])}
                    withoutPerm
                    cyId={cyId}
                    disabled={disabled}
                />
            ) : (
                <>
                    <Button
                        disabled={options.length == 0}
                        permCode={permCode}
                        icon={!isMenuOpen ? "chevron-down" : "chevron-up"}
                        text="Change status"
                        innerRef={buttonRef}
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                    />
                    {isMenuOpen &&
                        createPortal(
                            <ul
                                ref={ulRef}
                                className={styles.dropdown}
                                style={{
                                    top: buttonBox?.bottom ? buttonBox.bottom + TOP_OFFSET : undefined,
                                    left: buttonBox ? buttonBox.left : undefined,
                                }}
                            >
                                {options.map((item, index) => {
                                    return (
                                        <li key={`${index}-${item.value}`} onClick={() => handleStepChange(item)} className="pointer">
                                            <Icon name="arrow-right" />
                                            <span>{t(item.label)}</span>
                                        </li>
                                    );
                                })}
                            </ul>,
                            document.body
                        )}
                </>
            )}

            {selectedStep && (
                <CustomModalTemplate
                    handleStepSubmit={handleDefaultStepSubmit}
                    step={selectedStep}
                    handleCloseModal={handleCloseModal}
                    isBusy={isBusy}
                />
            )}
        </>
    );
};
